import { getApiErrorCode, getApiErrorMessage } from "@/lib/apiError";
import { notifyError, notifySuccess } from "@/lib/notification";
import * as offerService from "@/services/offer.service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { internshipsKey } from "./internship.queries";

export const offersKey = {
  all: ["offers"] as const,
  lists: (params?: { page?: number; limit?: number }) =>
    [...offersKey.all, "list", params] as const,
};

export const useOffersQuery = (
  params?: { page?: number; limit?: number },
  enabled = true
) =>
  useQuery({
    queryKey: offersKey.lists(params),
    queryFn: () => offerService.fetchOffers(params),
    enabled,
  });

export const useExtendOfferMutation = (internshipId: string) => {
  const queryClient = useQueryClient();

  const invalidateApplications = () =>
    queryClient.invalidateQueries({
      queryKey: [...internshipsKey.detail(internshipId), "applications"],
    });

  return useMutation({
    mutationFn: ({
      applicationId,
      data,
    }: {
      applicationId: string;
      data: offerService.OfferExtendType;
    }) => offerService.extendOffer(applicationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: offersKey.all });
      // Backend moves the application to `offer_extended` on its own.
      invalidateApplications();
      notifySuccess(
        "Offer extended",
        "The student has been notified by email and can see it in their offers."
      );
    },
    onError: (error: any) => {
      const code = getApiErrorCode(error);

      // Both 409s mean this screen is showing a stale status — another
      // recruiter got there first, or the application moved on. Refetch so the
      // row corrects itself and the button disappears.
      if (
        code === "OFFER_ALREADY_EXISTS" ||
        code === "OFFER_NOT_ALLOWED_FOR_STATUS"
      ) {
        queryClient.invalidateQueries({ queryKey: offersKey.all });
        invalidateApplications();
      }

      if (code === "OFFER_ALREADY_EXISTS") {
        notifyError(
          "This application already has a live offer",
          "Revoke the existing offer first if you need to change it."
        );
        return;
      }

      notifyError(getApiErrorMessage(error, "Failed to extend offer"));
    },
  });
};

export const useRevokeOfferMutation = (internshipId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (offerId: string) => offerService.revokeOffer(offerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: offersKey.all });
      // The application drops back to whatever status it held before the offer.
      queryClient.invalidateQueries({
        queryKey: [...internshipsKey.detail(internshipId), "applications"],
      });
      notifySuccess(
        "Offer revoked",
        "The student has been notified. You can now extend a corrected offer."
      );
    },
    onError: (error: any) => {
      // Fires when the student already accepted or declined — their answer is
      // final, so refetch to show the real state instead of a stale button.
      if (getApiErrorCode(error) === "INVALID_STATE_TRANSITION") {
        queryClient.invalidateQueries({ queryKey: offersKey.all });
        queryClient.invalidateQueries({
          queryKey: [...internshipsKey.detail(internshipId), "applications"],
        });
      }
      notifyError(getApiErrorMessage(error, "Failed to revoke offer"));
    },
  });
};
